import React, { useMemo } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { TitleText, BodyText, Card } from '../ui';
import { AvatarPhoto } from '../ui/AvatarPhoto';
import { cn } from '../../utils/cn';
import type { DietitianProfile } from '../../types/referral';
import { mapLanguageCodesToLabels } from '@/utils/languageUtils';

interface DietitianCardProps {
  dietitian: DietitianProfile;
  isSelected: boolean;
  onSelect: (dietitian: DietitianProfile) => void;
}

/**
 * Individual dietitian display card with all details from wireframe
 * Includes avatar, name, credentials, location, rating, availability, specialties, and bio
 */
export const DietitianCard: React.FC<DietitianCardProps> = ({
  dietitian,
  isSelected,
  onSelect,
}) => {
  const { isDark } = useTheme();
  const languageLabels = useMemo(
    () => mapLanguageCodesToLabels(dietitian.languagesSpoken || []),
    [dietitian.languagesSpoken],
  );

  const handleClick = () => {
    onSelect(dietitian);
  };



  return (
    <Card
      className={cn(
        'p-6 cursor-pointer transition-all duration-200 hover:shadow-lg relative',
        isDark ? 'bg-secondary-800 hover:bg-secondary-750' : 'bg-white hover:bg-secondary-50',
        isSelected && 'ring-2 ring-primary-500 shadow-lg',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
      )}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={`Select ${dietitian.name} as dietitian`}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center',
            'bg-primary-500 text-white text-sm font-bold'
          )}>
            ✓
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-start space-x-4 mb-4">
        {/* Avatar */}
        <AvatarPhoto
          src={dietitian.avatar}
          firstName={dietitian.name.split(' ')[0]}
          lastName={dietitian.name.split(' ').slice(1).join(' ')}
          size="lg"
          className="flex-shrink-0"
        />

        {/* Dietitian Info */}
        <div className="flex-1 min-w-0">
          <TitleText level={4} className="mb-1 leading-tight">
            {dietitian.name}
          </TitleText>
          <BodyText variant="secondary" size="sm" className="mb-2">
            {dietitian.credentials}
          </BodyText>

          {/* Location */}
          <div className="flex items-center space-x-1 mb-3">
            <span className="text-sm">📍</span>
            <BodyText variant="secondary" size="sm">
              {dietitian.location}
            </BodyText>
          </div>
        </div>
      </div>

      {/* Bio */}
      <BodyText variant="secondary" size="sm" className="mb-4 line-clamp-2">
        {dietitian.bio}
      </BodyText>

      {/* Specialties Section */}
      <div className="w-full">
        <div className="flex flex-wrap gap-2">
          {dietitian.specialties.slice(0, 3).map((specialty) => (
            <span
              key={specialty}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium',
                isDark
                  ? 'bg-primary-900 text-primary-300'
                  : 'bg-primary-100 text-primary-700'
              )}
            >
              {specialty}
            </span>
          ))}
          {dietitian.specialties.length > 3 && (
            <span
              className={cn(
                'px-2 py-1 rounded text-xs font-medium',
                isDark
                  ? 'bg-secondary-700 text-secondary-300'
                  : 'bg-secondary-200 text-secondary-600'
              )}
            >
              +{dietitian.specialties.length - 3} more
            </span>
          )}
        </div>
      </div>

      {languageLabels.length > 0 && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2" role="list" aria-label="Languages spoken">
            {languageLabels.map((label) => (
              <span
                key={label}
                role="listitem"
                className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
                  isDark
                    ? 'bg-secondary-800/80 text-secondary-100 border-secondary-600'
                    : 'bg-secondary-100 text-secondary-700 border-secondary-200'
                )}
                aria-label={`Speaks ${label}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};